import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { message } from 'antd';
import { formatPhoneNumber } from '@leedpulse-monorepo/utils';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import CompanyContext from '../../../contexts/company/CompanyContext';
import SubscriptionContext from '../../../contexts/subscription/SubscriptionContext';
import { getCompanyAiReceptionistDetail } from '../../../../domain/use_cases/ai-receptionist/getCompanyAiReceptionistDetail';
import { updateCompanyAiReceptionistDetail } from '../../../../domain/use_cases/ai-receptionist/updateCompanyAiReceptionistDetail';
import {
  AiReceptionistDetail,
  AiReceptionistMotif,
  AiReceptionistTriggers,
  DEFAULT_AI_RECEPTIONIST_TRIGGERS,
} from '../../../../domain/models/AiReceptionist';
import { buildDefaultGreeting, isDefaultGreeting } from '../receptionistAi.helpers';
import { getReceptionistAiStatus } from '../types/ReceptionistAiDisplay';

type MotifModes = Record<string, { mode: 'automatic' | 'custom'; customInstruction: string }>;

const useReceptionistAiDetailPageController = () => {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const { authentication } = useContext(AuthenticationContext);
  const { activeCompany, users } = useContext(CompanyContext);
  const { subscriptions } = useContext(SubscriptionContext);

  const [detail, setDetail] = useState<AiReceptionistDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [assistantName, setAssistantName] = useState('Donna');
  const [represents, setRepresents] = useState('');
  const [transmitsTo, setTransmitsTo] = useState('');
  const [greeting, setGreetingState] = useState('');
  const [isGreetingSynced, setIsGreetingSynced] = useState(true);
  const [ringCount, setRingCount] = useState(7);
  const [triggers, setTriggers] = useState<AiReceptionistTriggers>(DEFAULT_AI_RECEPTIONIST_TRIGGERS);
  const [activities, setActivities] = useState<string[]>([]);
  const [motifModes, setMotifModes] = useState<MotifModes>({});
  const [instructions, setInstructions] = useState('');

  const seeded = useRef(false);

  const subscription = subscriptions.find((sub) => sub.id === subscriptionId);
  const assignedUser = subscription ? users.find((user) => user.idSubscription?.[subscription.id]) : undefined;
  const userDisplayName = assignedUser ? `${assignedUser.firstname} ${assignedUser.lastname}`.trim() : '';
  const userFirstName = assignedUser?.firstname || '';
  const subscriptionPhone = subscription?.sender?.[0] ? formatPhoneNumber(subscription.sender[0]) : '';
  const headerSubtitle = [userDisplayName, activeCompany?.name, subscriptionPhone].filter(Boolean).join(' · ');

  const fetchDetail = useCallback(async () => {
    if (!activeCompany?.id || !authentication || !subscriptionId) return;
    setIsLoading(true);
    seeded.current = false;
    try {
      const response = await getCompanyAiReceptionistDetail(
        { company_id: activeCompany.id, subscription_id: subscriptionId },
        authentication,
      );
      setDetail(response);
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Erreur lors du chargement du réceptionniste IA';
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany?.id, authentication, subscriptionId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    if (seeded.current || !detail) return;
    seeded.current = true;

    const { profile, config, catalog } = detail;
    setEnabled(profile.enabled);
    setAssistantName(profile.fullname || 'Donna');
    setRepresents(profile.public_label || '');
    setTransmitsTo(profile.callback_label || '');
    setRingCount(profile.ring_count || 7);
    setTriggers({ ...DEFAULT_AI_RECEPTIONIST_TRIGGERS, ...profile.triggers });
    const labels = {
      assistantName: profile.fullname || 'Donna',
      represents: profile.public_label || '',
      transmitsTo: profile.callback_label || '',
    };
    const bSynced = !profile.first_message || isDefaultGreeting(profile.first_message, catalog.first_message, labels);
    setGreetingState(bSynced ? buildDefaultGreeting(catalog.first_message, labels) : profile.first_message);
    setIsGreetingSynced(bSynced);
    setActivities(config.activities.filter((activity) => activity.enabled).map((activity) => activity.activity_id));

    const modes: MotifModes = {};
    catalog.motifs.forEach((motif) => {
      const saved = config.motifs.find((cm) => cm.motif_id === motif.id);
      modes[motif.id] = saved
        ? { mode: saved.mode, customInstruction: saved.custom_instruction ?? '' }
        : { mode: 'automatic', customInstruction: '' };
    });
    setMotifModes(modes);
    setInstructions(config.other || '');
  }, [detail]);

  const toggleActivity = (activityId: string) => {
    setActivities((prev) => (prev.includes(activityId) ? prev.filter((id) => id !== activityId) : [...prev, activityId]));
  };

  // At least one case stays checked: with none, Donna would never pick up while shown as enabled
  const toggleTrigger = (key: keyof AiReceptionistTriggers) => {
    setTriggers((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      return Object.values(next).some(Boolean) ? next : prev;
    });
  };

  const setMotifMode = (motifId: string, mode: 'automatic' | 'custom', customInstruction: string) => {
    setMotifModes((prev) => ({ ...prev, [motifId]: { mode, customInstruction } }));
  };

  const setGreeting = (value: string) => {
    setGreetingState(value);
    setIsGreetingSynced(false);
  };

  const applyDefaultGreeting = () => {
    if (!detail) return;
    setGreetingState(buildDefaultGreeting(detail.catalog.first_message, { assistantName, represents, transmitsTo }));
    setIsGreetingSynced(true);
  };

  useEffect(() => {
    if (!detail || !isGreetingSynced) return;
    setGreetingState(buildDefaultGreeting(detail.catalog.first_message, { assistantName, represents, transmitsTo }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistantName, represents, transmitsTo]);

  const isMotifCustom = (motifId: string) => {
    const state = motifModes[motifId];
    return state?.mode === 'custom' && !!state.customInstruction.trim();
  };

  const activityGroups = detail
    ? detail.catalog.activities.map((activity) => ({
        activity,
        motifs: detail.catalog.motifs.filter((motif) => motif.activities.includes(activity.id)),
      }))
    : [];

  const alwaysMotifs = detail ? detail.catalog.motifs.filter((motif) => motif.activities.length === 0) : [];

  const applicableMotifs = detail
    ? detail.catalog.motifs.filter(
        (motif) => motif.activities.length === 0 || motif.activities.some((id) => activities.includes(id)),
      )
    : [];

  const applicableMotifsCount = applicableMotifs.length;
  const customMotifsCount = applicableMotifs.filter((motif) => isMotifCustom(motif.id)).length;
  const selectedActivitiesLabel = detail
    ? detail.catalog.activities
        .filter((activity) => activities.includes(activity.id))
        .map((activity) => activity.label)
        .join(' · ')
    : '';

  const status = detail ? getReceptionistAiStatus({ enabled }) : null;

  const handleSave = async () => {
    if (!detail || !activeCompany?.id || !authentication || !subscriptionId) return;

    const motifs: AiReceptionistMotif[] = applicableMotifs.map((motif) => {
      const state = motifModes[motif.id] ?? { mode: 'automatic' as const, customInstruction: '' };
      return state.mode === 'custom' && state.customInstruction.trim()
        ? { motif_id: motif.id, mode: 'custom', custom_instruction: state.customInstruction.trim() }
        : { motif_id: motif.id, mode: 'automatic' };
    });

    setIsSaving(true);
    try {
      const response = await updateCompanyAiReceptionistDetail(
        {
          company_id: activeCompany.id,
          subscription_id: subscriptionId,
          profile: {
            fullname: assistantName.trim(),
            // Default greeting sent as the template: the backend fills the labels on every call
            first_message: isGreetingSynced && detail.catalog.first_message ? detail.catalog.first_message : greeting.trim(),
            public_label: represents.trim(),
            callback_label: transmitsTo.trim(),
            ring_count: ringCount,
            ...(detail.profile.triggers && { triggers }),
            enabled,
            is_provisioned: detail.profile.is_provisioned,
          },
          config: {
            activities: detail.catalog.activities.map((activity) => ({
              activity_id: activity.id,
              enabled: activities.includes(activity.id),
            })),
            motifs,
            other: instructions.trim(),
          },
        },
        authentication,
      );
      setDetail({
        ...detail,
        profile: { ...response.profile, is_provisioned: detail.profile.is_provisioned },
        config: response.config,
      });
      message.success('Réglages enregistrés');
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Impossible d’enregistrer ces réglages';
      message.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    state: {
      detail,
      isLoading,
      isSaving,
      userDisplayName,
      userFirstName,
      headerSubtitle,
      status,
      enabled,
      assistantName,
      represents,
      transmitsTo,
      greeting,
      ringCount,
      triggers,
      activities,
      motifModes,
      instructions,
      activityGroups,
      alwaysMotifs,
      applicableMotifsCount,
      customMotifsCount,
      selectedActivitiesLabel,
    },
    actions: {
      setEnabled,
      setAssistantName,
      setRepresents,
      setTransmitsTo,
      setGreeting,
      setRingCount,
      toggleTrigger,
      toggleActivity,
      setMotifMode,
      setInstructions,
      applyDefaultGreeting,
      handleSave,
      refresh: fetchDetail,
    },
  };
};

export default useReceptionistAiDetailPageController;
